import { useEffect, useId, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowUp,
  Bold,
  Check,
  ChevronDown,
  Italic,
  Languages,
  Minus,
  Plus,
  Square,
  SquareDashed,
  SquareSlash,
} from "lucide-react";

import { cn } from "lib/utils";
import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";

// "default" leaves font-family unset so the existing :lang(ar) rule in
// app.css keeps picking Arabic vs. Latin automatically — shown on its own,
// outside both tabs, since it's the one choice that's correct for either
// script. Every other entry is a specific family loaded in
// public/index.html's Google Fonts link, grouped by which script it's
// actually designed for (an English display face doesn't have Arabic
// glyphs, and vice versa) so the tabs are a real filter, not decoration.
export const CAPTION_FONTS = {
  default: { label: "Default", value: undefined, group: null },

  mono: { label: "Mono", value: "var(--font-mono)", group: "english" },
  montserrat: { label: "Montserrat", value: "'Montserrat', var(--font-sans)", group: "english" },
  poppins: { label: "Poppins", value: "'Poppins', var(--font-sans)", group: "english" },
  oswald: { label: "Oswald", value: "'Oswald', var(--font-sans)", group: "english" },
  bebas: { label: "Bebas Neue", value: "'Bebas Neue', var(--font-sans)", group: "english" },
  anton: { label: "Anton", value: "'Anton', var(--font-sans)", group: "english" },
  playfair: { label: "Playfair Display", value: "'Playfair Display', var(--font-sans)", group: "english" },
  robotoCondensed: { label: "Roboto Condensed", value: "'Roboto Condensed', var(--font-sans)", group: "english" },

  cairo: { label: "Cairo", value: "'Cairo', var(--font-arabic)", group: "arabic" },
  tajawal: { label: "Tajawal", value: "'Tajawal', var(--font-arabic)", group: "arabic" },
  almarai: { label: "Almarai", value: "'Almarai', var(--font-arabic)", group: "arabic" },
  amiri: { label: "Amiri", value: "'Amiri', var(--font-arabic)", group: "arabic" },
  changa: { label: "Changa", value: "'Changa', var(--font-arabic)", group: "arabic" },
  elMessiri: { label: "El Messiri", value: "'El Messiri', var(--font-arabic)", group: "arabic" },
  reemKufi: { label: "Reem Kufi", value: "'Reem Kufi', var(--font-arabic)", group: "arabic" },
  lalezar: { label: "Lalezar", value: "'Lalezar', var(--font-arabic)", group: "arabic" },
  rakkas: { label: "Rakkas", value: "'Rakkas', var(--font-arabic)", group: "arabic" },
};

// The export API's `font` field only accepts these four (see the backend
// brief) — everything else above is preview-only until that contract grows
// to match. Exporting with an unlisted font substitutes the closest
// supported family for its script rather than failing the request or
// silently mismatching what render actually gets.
const EXPORT_SUPPORTED_FONTS = new Set(["default", "mono", "cairo", "tajawal"]);
export function resolveExportFont(fontId) {
  if (EXPORT_SUPPORTED_FONTS.has(fontId)) return fontId;
  return CAPTION_FONTS[fontId]?.group === "arabic" ? "cairo" : "default";
}

export const CAPTION_COLORS = [
  { name: "White", value: "#ffffff" },
  { name: "Yellow", value: "#fde047" },
  { name: "Sky", value: "#38bdf8" },
  { name: "Black", value: "#000000" },
];

// "box" = a solid band behind the text; "outline" = a stroked/shadowed edge
// with no fill, so the video stays visible right up to the letterforms;
// "none" = plain text with just enough shadow to stay legible.
// backgroundColor doubles as the outline's stroke color in outline mode —
// there's no fill to speak of there, so the "background" a user is
// choosing is the stroke.
export const BACKGROUND_MODES = ["box", "outline", "none"];

export const TEXT_ALIGNS = ["left", "center", "right"];

const MIN_SIZE = 14;
const MAX_SIZE = 40;
const SIZE_STEP = 2;

// Keeps the caption a little short of the true top/bottom edge even at the
// slider's extremes — a flush 0/100 read as a bug (text sliced by the
// frame), not a choice.
const MIN_POSITION = 4;
const MAX_POSITION = 96;

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export const DEFAULT_CAPTION_STYLE = {
  font: "default",
  size: 22,
  bold: false,
  italic: false,
  textAlign: "center",
  positionY: 90,
  color: CAPTION_COLORS[0].value,
  backgroundMode: "box",
  backgroundColor: "#000000",
  backgroundOpacity: 70,
};

// Renders the caption text's own look (font/weight/color/stroke/shadow) —
// shared between the real overlay in TranscriptEditorPage and anywhere else
// that needs to preview it, so there's exactly one place this logic lives.
// Deliberately excludes textAlign and the background band itself (those are
// layout concerns of whatever wraps the text, not the text's own styling).
export function captionTextStyle(style) {
  const bgColor = style.backgroundColor || "#000000";
  return {
    fontFamily: CAPTION_FONTS[style.font]?.value,
    fontWeight: style.bold ? 700 : 500,
    fontStyle: style.italic ? "italic" : "normal",
    color: style.color,
    WebkitTextStroke: style.backgroundMode === "outline" ? `1px ${bgColor}` : undefined,
    textShadow:
      style.backgroundMode === "none"
        ? "0 1px 3px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)"
        : style.backgroundMode === "outline"
          ? "0 1px 2px rgba(0,0,0,0.6)"
          : undefined,
  };
}

// Matches BOX_PADDING_PX in the export renderer (domains/export/renderer.py) —
// both are authored at a 1080px-tall reference frame and scaled by the same
// height/1080 factor, so the padding-to-font-size ratio stays in sync between
// the preview and the export.
const BOX_PADDING_PX = 6;

// The band behind the text in "box" mode — separate from captionTextStyle
// since it applies to the container, not the text itself.
export function captionBackgroundStyle(style, scale = 1) {
  if (style.backgroundMode !== "box") return {};
  const opacity = (style.backgroundOpacity ?? 70) / 100;
  const [r, g, b] = hexToRgb(style.backgroundColor || "#000000");
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, ${opacity})`,
    padding: `${BOX_PADDING_PX * scale}px`,
  };
}

function hexToRgb(hex) {
  const normalized = HEX_RE.test(hex) ? hex : "#000000";
  const int = parseInt(normalized.slice(1), 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

// Same shape as TranscriptEditorPage's own LineActionButton (icon + tooltip
// + active state), just themed for this panel's always-black surface — the
// video letterboxing area stays black regardless of light/dark theme, same
// reasoning as the dashed drop-zone in upload-dialog.js using bare white/*
// utilities instead of the theme's border/muted-foreground tokens.
function ToggleButton({ icon: Icon, label, active, onClick }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-slot="button"
          aria-label={label}
          aria-pressed={active}
          onClick={onClick}
          className={cn(
            "flex size-7 shrink-0 appearance-none items-center justify-center rounded border-0 bg-transparent text-white/60 outline-none transition-colors",
            "hover:bg-white/10 hover:text-white focus-visible:ring-1 focus-visible:ring-ring",
            active && "bg-white/15 text-white"
          )}
        >
          <Icon className="size-3.5" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

const BACKGROUND_MODE_ICON = { box: Square, outline: SquareDashed, none: SquareSlash };
const TEXT_ALIGN_ICON = { left: AlignLeft, center: AlignCenter, right: AlignRight };

// A single swatch trigger (native title/aria-label for the hover hint —
// no Radix Tooltip layered on top of the Popover trigger, since nesting two
// asChild-composed triggers on one element is exactly the kind of thing
// worth not risking for a hover hint alone) that opens a small panel with
// quick presets plus a hex field, so an exact color is always reachable
// without a permanently-visible swatch row crowding the panel. Reused for
// both the text color and the background/outline color.
function ColorPicker({ label, color, onChange }) {
  const { language } = useLanguage();
  const tr = translations[language].captionStyle;
  const [hexDraft, setHexDraft] = useState(color);
  const [open, setOpen] = useState(false);
  const hexInputId = useId();

  // Re-seed the draft from the real value whenever the popover opens (picks
  // up a swatch click made elsewhere, and discards any unsubmitted typing
  // from last time it was open).
  useEffect(() => {
    if (open) setHexDraft(color);
  }, [open, color]);

  function commitHex(raw) {
    const normalized = raw.trim().replace(/^#?/, "#");
    if (HEX_RE.test(normalized)) onChange(normalized);
    else setHexDraft(color);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="button"
          title={label}
          aria-label={label}
          style={{ backgroundColor: color }}
          className="size-5 shrink-0 appearance-none rounded-full border border-white/30 p-0 outline-none transition-transform hover:scale-110 focus-visible:ring-1 focus-visible:ring-ring"
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-[10px] font-normal uppercase tracking-wide text-muted-foreground">{tr.presets}</Label>
            <div className="flex flex-wrap gap-2">
              {CAPTION_COLORS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  data-slot="button"
                  aria-label={`${label}: ${tr.colorNames[preset.name] ?? preset.name}`}
                  aria-pressed={color === preset.value}
                  onClick={() => onChange(preset.value)}
                  style={{ backgroundColor: preset.value }}
                  className={cn(
                    "size-7 shrink-0 appearance-none rounded-full border border-border p-0 outline-none transition-shadow",
                    color === preset.value && "ring-2 ring-ring ring-offset-2 ring-offset-popover"
                  )}
                />
              ))}
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={hexInputId} className="text-[10px] font-normal uppercase tracking-wide text-muted-foreground">
              {tr.hex}
            </Label>
            <div className="flex items-center gap-2">
              <span className="size-8 shrink-0 rounded-md border border-border" style={{ backgroundColor: color }} />
              <Input
                id={hexInputId}
                value={hexDraft}
                onChange={(event) => setHexDraft(event.target.value)}
                onBlur={(event) => commitHex(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                }}
                placeholder="#ffffff"
                className="h-8 font-mono text-xs"
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const ENGLISH_FONTS = Object.entries(CAPTION_FONTS).filter(([, font]) => font.group === "english");
const ARABIC_FONTS = Object.entries(CAPTION_FONTS).filter(([, font]) => font.group === "arabic");

const PANEL_BG = "#111214";
const PANEL_BORDER = "rgba(255,255,255,0.10)";
const ROW_ACTIVE_BG = "rgba(255,255,255,0.10)";
const ROW_HOVER_BG = "rgba(255,255,255,0.06)";

function CaptionLanguagePicker({ options, selected, enabled, loading, onChange }) {
  const { language } = useLanguage();
  const tr = translations[language].captionStyle;
  const [open, setOpen] = useState(false);
  const selectedSet = new Set(selected);
  const optionByCode = new Map(options.map((option) => [option.code, option]));
  const orderedOptions = [
    ...selected.map((code) => optionByCode.get(code)).filter(Boolean),
    ...options.filter((option) => !selectedSet.has(option.code)),
  ];

  function toggle(code) {
    if (selectedSet.has(code)) {
      if (selected.length === 1) return;
      onChange(selected.filter((item) => item !== code));
    } else {
      onChange([...selected, code]);
    }
  }

  function move(code, delta) {
    const from = selected.indexOf(code);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= selected.length) return;
    const next = [...selected];
    [next[from], next[to]] = [next[to], next[from]];
    onChange(next);
  }

  const summary = selected.map((code) => optionByCode.get(code)?.label ?? code.toUpperCase()).join(" → ");

  return (
    <div className="grid gap-1">
      <Label className="text-[10px] font-normal uppercase tracking-wide text-white/40">{tr.videoLanguages}</Label>
      <Popover open={enabled && open} onOpenChange={(next) => enabled && setOpen(next)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-slot="button"
            disabled={!enabled}
            title={enabled ? tr.chooseLanguagesTitle : tr.chooseLanguagesDisabledTitle}
            className="flex h-8 w-full items-center gap-2 rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white outline-none transition-colors hover:bg-white/10 focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Languages className="size-3.5 shrink-0 text-white/60" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-start">{summary || tr.originalLanguage}</span>
            <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-64 overflow-hidden rounded-xl p-0 shadow-2xl"
          style={{ background: PANEL_BG, border: `1px solid ${PANEL_BORDER}` }}
        >
          <div className="border-b border-white/10 px-3 py-2">
            <p className="text-xs font-medium text-white">{tr.captionOrderTitle}</p>
            <p className="text-[10px] text-white/40">{tr.captionOrderHint}</p>
          </div>
          <div className="grid gap-0.5 p-1.5">
            {orderedOptions.map((option) => {
              const active = selectedSet.has(option.code);
              const position = selected.indexOf(option.code);
              const isLoading = loading?.has(option.code);
              return (
                <div
                  key={option.code}
                  className={cn(
                    "flex items-center gap-1 rounded-md",
                    active ? "bg-white/10" : "hover:bg-white/[0.06]"
                  )}
                >
                  <button
                    type="button"
                    data-slot="button"
                    aria-pressed={active}
                    onClick={() => toggle(option.code)}
                    className="flex min-w-0 flex-1 items-center gap-2 border-0 bg-transparent px-2 py-2 text-start text-xs text-white/70 outline-none hover:text-white focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <span className="flex size-4 shrink-0 items-center justify-center rounded border border-white/25">
                      {active ? <Check className="size-3" aria-hidden="true" /> : null}
                    </span>
                    <span className="truncate">{option.label}</span>
                    {isLoading ? <span className="ms-auto text-[10px] text-white/40">{tr.loading}</span> : null}
                  </button>
                  {active ? (
                    <div className="flex shrink-0 items-center pe-1">
                      <button
                        type="button"
                        data-slot="button"
                        aria-label={tr.moveUp(option.label)}
                        disabled={position === 0}
                        onClick={() => move(option.code, -1)}
                        className="flex size-6 items-center justify-center rounded border-0 bg-transparent text-white/50 outline-none hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-20"
                      >
                        <ArrowUp className="size-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        data-slot="button"
                        aria-label={tr.moveDown(option.label)}
                        disabled={position === selected.length - 1}
                        onClick={() => move(option.code, 1)}
                        className="flex size-6 items-center justify-center rounded border-0 bg-transparent text-white/50 outline-none hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-20"
                      >
                        <ArrowDown className="size-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FontPicker({ font, onChange }) {
  const { language } = useLanguage();
  const tr = translations[language].captionStyle;
  const langTr = translations[language].transcribe;
  const [open, setOpen] = useState(false);
  const [script, setScript] = useState(CAPTION_FONTS[font]?.group === "arabic" ? "arabic" : "english");

  function select(id) {
    onChange(id);
    setOpen(false);
  }

  const fonts = script === "arabic" ? ARABIC_FONTS : ENGLISH_FONTS;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="button"
          className="flex h-8 w-full items-center justify-between rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white outline-none transition-colors hover:bg-white/10 focus-visible:ring-1 focus-visible:ring-ring"
        >
          <span style={{ fontFamily: CAPTION_FONTS[font].value }} className="truncate">
            {font === "default" ? tr.defaultFont : CAPTION_FONTS[font].label}
          </span>
          <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-52 overflow-hidden rounded-xl p-0 shadow-2xl"
        style={{ background: PANEL_BG, border: `1px solid ${PANEL_BORDER}` }}
      >
        {/* Script toggle */}
        <div style={{ borderBottom: `1px solid ${PANEL_BORDER}`, display: "flex" }}>
          {[["english", langTr.languageEn], ["arabic", langTr.languageAr]].map(([s, label]) => (
            <button
              key={s}
              type="button"
              data-slot="button"
              onClick={() => setScript(s)}
              style={{
                flex: 1,
                padding: "7px 0",
                fontSize: 12,
                fontWeight: 500,
                color: script === s ? "#fff" : "rgba(255,255,255,0.38)",
                background: "transparent",
                border: "none",
                borderBottom: script === s ? "2px solid rgba(255,255,255,0.7)" : "2px solid transparent",
                cursor: "pointer",
                transition: "color 0.15s, border-color 0.15s",
                outline: "none",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Default option */}
        <button
          type="button"
          data-slot="button"
          onClick={() => select("default")}
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            fontSize: 13,
            color: font === "default" ? "#fff" : "rgba(255,255,255,0.55)",
            background: font === "default" ? ROW_ACTIVE_BG : "transparent",
            border: "none",
            borderBottom: `1px solid ${PANEL_BORDER}`,
            cursor: "pointer",
            transition: "background 0.12s, color 0.12s",
            outline: "none",
            textAlign: "start",
          }}
          onMouseEnter={(e) => { if (font !== "default") e.currentTarget.style.background = ROW_HOVER_BG; }}
          onMouseLeave={(e) => { if (font !== "default") e.currentTarget.style.background = "transparent"; }}
        >
          {tr.defaultFont}
          {font === "default" && <Check style={{ width: 13, height: 13, color: "#fff", flexShrink: 0 }} aria-hidden="true" />}
        </button>

        {/* Font list */}
        <div style={{ maxHeight: 200, overflowY: "auto" }}>
          {fonts.map(([id, f]) => (
            <button
              key={id}
              type="button"
              data-slot="button"
              onClick={() => select(id)}
              style={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                fontSize: 13,
                fontFamily: f.value,
                color: font === id ? "#fff" : "rgba(255,255,255,0.55)",
                background: font === id ? ROW_ACTIVE_BG : "transparent",
                border: "none",
                cursor: "pointer",
                transition: "background 0.12s, color 0.12s",
                outline: "none",
                textAlign: "start",
              }}
              onMouseEnter={(e) => { if (font !== id) e.currentTarget.style.background = ROW_HOVER_BG; }}
              onMouseLeave={(e) => { if (font !== id) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.label}
              </span>
              {font === id && <Check style={{ width: 13, height: 13, color: "#fff", flexShrink: 0 }} aria-hidden="true" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Compact styling controls for the live caption overlay on a caption-source
 * transcript's video preview — ordered caption-language selection, font (a
 * tabbed English/Arabic picker), size, text color, weight/emphasis, text
 * alignment, a continuous vertical placement slider, and a Background
 * section (mode, color, opacity).
 * `style` is one of the plain objects shaped like DEFAULT_CAPTION_STYLE;
 * `onChange` receives the whole next object (this component has no state of
 * its own beyond the color popovers' in-progress hex drafts). It is not sent
 * to the backend as-is: see resolveExportFont; textAlign remains preview-only,
 * while backgroundColor/backgroundOpacity are included in video exports.
 */
export function CaptionStylePanel({
  style,
  onChange,
  languageOptions = [],
  selectedLanguages = [],
  languagesEnabled = false,
  loadingLanguages,
  onLanguagesChange,
}) {
  const { language } = useLanguage();
  const tr = translations[language].captionStyle;

  function patch(update) {
    onChange({ ...style, ...update });
  }

  const showsBackgroundColor = style.backgroundMode !== "none";

  const BACKGROUND_MODE_LABEL = {
    box: tr.backgroundBox,
    outline: tr.backgroundOutline,
    none: tr.backgroundNone,
  };
  const TEXT_ALIGN_LABEL = {
    left: tr.alignLeft,
    center: tr.alignCenter,
    right: tr.alignRight,
  };

  return (
    <div className="grid w-full gap-2.5 border-t border-white/10 pt-3">
      <CaptionLanguagePicker
        options={languageOptions}
        selected={selectedLanguages}
        enabled={languagesEnabled}
        loading={loadingLanguages}
        onChange={onLanguagesChange}
      />

      {/* Font · size · color · bold · italic · alignment — all in one compact row */}
      <div className="flex flex-wrap items-center gap-1">
        <div className="min-w-0 flex-1">
          <FontPicker font={style.font} onChange={(font) => patch({ font })} />
        </div>

        <div className="flex h-8 shrink-0 items-center rounded-md border border-white/15 bg-white/5 px-0.5">
          <button
            type="button"
            data-slot="button"
            aria-label={tr.decreaseSize}
            disabled={style.size <= MIN_SIZE}
            onClick={() => patch({ size: Math.max(MIN_SIZE, style.size - SIZE_STEP) })}
            className="flex size-6 shrink-0 appearance-none items-center justify-center rounded border-0 bg-transparent text-white/60 outline-none transition-colors hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-30"
          >
            <Minus className="size-3.5" aria-hidden="true" />
          </button>
          <span className="w-6 text-center font-mono text-xs tabular-nums text-white">{style.size}</span>
          <button
            type="button"
            data-slot="button"
            aria-label={tr.increaseSize}
            disabled={style.size >= MAX_SIZE}
            onClick={() => patch({ size: Math.min(MAX_SIZE, style.size + SIZE_STEP) })}
            className="flex size-6 shrink-0 appearance-none items-center justify-center rounded border-0 bg-transparent text-white/60 outline-none transition-colors hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-30"
          >
            <Plus className="size-3.5" aria-hidden="true" />
          </button>
        </div>

        <ColorPicker label={tr.textColor} color={style.color} onChange={(color) => patch({ color })} />
        <ToggleButton icon={Bold} label={tr.bold} active={style.bold} onClick={() => patch({ bold: !style.bold })} />
        <ToggleButton icon={Italic} label={tr.italic} active={style.italic} onClick={() => patch({ italic: !style.italic })} />

        <div className="h-4 w-px shrink-0 bg-white/15" />

        {TEXT_ALIGNS.map((align) => (
          <ToggleButton
            key={align}
            icon={TEXT_ALIGN_ICON[align]}
            label={TEXT_ALIGN_LABEL[align]}
            active={style.textAlign === align}
            onClick={() => patch({ textAlign: align })}
          />
        ))}
      </div>

      <div className="grid gap-1">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] font-normal uppercase tracking-wide text-white/40">{tr.placement}</Label>
          <span className="font-mono text-[10px] text-white/40">{style.positionY < 50 ? tr.top : tr.bottom}</span>
        </div>
        <input
          type="range"
          min={MIN_POSITION}
          max={MAX_POSITION}
          value={style.positionY}
          onChange={(event) => patch({ positionY: Number(event.target.value) })}
          aria-label={tr.verticalPlacement}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/15 accent-white"
        />
      </div>

      <div className="grid gap-1.5 border-t border-white/10 pt-2.5">
        <Label className="text-[10px] font-normal uppercase tracking-wide text-white/40">{tr.background}</Label>
        <div className="flex items-center gap-0.5">
          {BACKGROUND_MODES.map((mode) => (
            <ToggleButton
              key={mode}
              icon={BACKGROUND_MODE_ICON[mode]}
              label={BACKGROUND_MODE_LABEL[mode]}
              active={style.backgroundMode === mode}
              onClick={() => patch({ backgroundMode: mode })}
            />
          ))}
        </div>

        {showsBackgroundColor ? (
          <div className="flex items-center gap-2.5">
            <ColorPicker
              label={style.backgroundMode === "outline" ? tr.outlineColor : tr.backgroundColorLabel}
              color={style.backgroundColor}
              onChange={(backgroundColor) => patch({ backgroundColor })}
            />
            {style.backgroundMode === "box" ? (
              <div className="flex flex-1 items-center gap-2">
                <input
                  type="range"
                  min={10}
                  max={100}
                  value={style.backgroundOpacity}
                  onChange={(event) => patch({ backgroundOpacity: Number(event.target.value) })}
                  aria-label={tr.backgroundOpacity}
                  className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/15 accent-white"
                />
                <span className="w-8 shrink-0 text-end font-mono text-[10px] text-white/40">
                  {style.backgroundOpacity}%
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
